import { useEffect, useState, useMemo, useCallback } from "react";
import {
  useGetAllVehicleQuery,
  useDeleteVehicleMutation,
} from "../../../Data/services/vehicleApi";
import Toast, { SuccessToast } from "../../../Hooks/useToast";
import { IoMdEye } from "react-icons/io";
import { RiEditFill } from "react-icons/ri";
import { MdDeleteForever } from "react-icons/md";
import ToolTip from "../../../Components/CustomComponents/ToolTip";
import { useNavigate } from "react-router-dom";
import { WEB_ROUTES } from "../../../constants";
import { useSelector, useDispatch } from "react-redux";
import {
  setCurrentPage as setReduxCurrentPage,
  setPageSize as setReduxPageSize,
  setSearchQuery as setReduxSearchQuery,
} from "../../../Data/features/vehicleManagementSlice";

const useVehicleManagement = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { currentPage, pageSize, searchQuery } = useSelector(
    (state) => state.vehicleManagement
  );

  const [editedData, setEditedData] = useState("");
  const [detailModal, setDetailModal] = useState(false);
  const [reviewModal, setReviewModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [dataFetched, setDataFetched] = useState(false);
  const [rowData, setRowData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [indicationMessage, setIndicationMessage] = useState("loading...");

  const setCurrentPage = (page) => dispatch(setReduxCurrentPage(page));
  const setPageSize = (size) => dispatch(setReduxPageSize(size));
  const setSearchQuery = (query) => dispatch(setReduxSearchQuery(query));

  const { data, isSuccess, isLoading, isFetching, refetch } =
    useGetAllVehicleQuery(
      {
        search: searchQuery.length >= 3 ? searchQuery : "",
        limit: pageSize,
        page: currentPage,
      },
      { refetchOnMountOrArgChange: true }
    );

  useEffect(() => {
    if (data) {
      setPageSize(data.links.per_page);
      setCurrentPage(data.links.current);
      setTotalRecords(data.links.total_records);
      setTotalPages(data.links.total);
      setRowData(data.data);
      setDataFetched(true);

      if (data.data.length === 0) {
        setIndicationMessage("No categories found");
      }
    }
  }, [data, dispatch]);

  const handleSearchChange = useCallback(
    (event) => {
      setSearchQuery(event.target.value);
    },
    [setSearchQuery]
  );

  const handleDeleteModalOpen = useCallback(() => {
    setDeleteModal(true);
  }, []);

  const handleDeleteModalClose = useCallback(() => {
    setDeleteModal(false);
    setEditedData("");
  }, []);

  const handleDetailModalOpen = useCallback(() => {
    setDetailModal(true);
  }, []);

  const handleDetailModalClose = useCallback(() => {
    setDetailModal(false);
    setEditedData("");
  }, []);

  const handleReviewModalOpen = useCallback(() => {
    setReviewModal(true);
  }, []);

  const handleReviewModalClose = useCallback(() => {
    setReviewModal(false);
    setEditedData("");
  }, []);

  const handleEditModalOpen = useCallback(() => {
    setEditModal(true);
  }, []);

  const handleEditModalClose = useCallback(() => {
    setEditModal(false);
    setEditedData("");
  }, []);

  const handleAddUser = useCallback(() => {
    setEditedData(null);
    setEditModal(true);
  }, []);

  const [deleteVehicle, { isLoading: isDeleting }] = useDeleteVehicleMutation();

  const deleteData = useCallback(
    async (slug) => {
      try {
        const { data } = await deleteVehicle(slug);
        if (data.code === 200) {
          await refetch();
          handleDeleteModalClose();
          SuccessToast(data.message);
        }
      } catch (error) {
        Toast(error.message, "error");
      }
    },
    [deleteVehicle, refetch, handleDeleteModalClose]
  );

  const indication = useCallback(() => {
    return indicationMessage;
  }, [indicationMessage]);

  const columns = useMemo(
    () => [
      {
        dataField: "attachments[0].media_url",
        text: "Image",
        headerStyle: {
          width: "80px",
        },
        formatter: (cellContent, row) => {
          return (
            <img
              alt="user"
              src={cellContent || "images/no_image_available.png"}
              loading="lazy"
              width={62}
              height={62}
              className="rounded-3 object-fit-cover"
            />
          );
        },
      },
      {
        dataField: "name",
        text: "Name",
        headerStyle: {
          width: "180px",
        },
        formatter: (cellContent, row) => {
          return <p className="text-wrap-custom">{cellContent}</p>;
        },
      },
      {
        dataField: "model",
        text: "Model",
        headerStyle: {
          width: "180px",
        },
        formatter: (cellContent, row) => {
          return <p className="text-wrap-custom">{cellContent}</p>;
        },
      },
      {
        dataField: "rental_type",
        text: "Type",
        headerStyle: {
          width: "100px",
        },
        formatter: (cellContent, row) => {
          return <p className="text-wrap-custom">{cellContent}</p>;
        },
      },
      {
        dataField: "rental_price",
        text: "Price",
        headerStyle: {
          width: "100px",
        },
        formatter: (cellContent, row) => {
          return <p className="text-wrap-custom">$ {cellContent}</p>;
        },
      },
      {
        dataField: "engine_capacity",
        text: "Engine",
        headerStyle: {
          width: "100px",
        },
        formatter: (cellContent, row) => {
          return <p className="text-wrap-custom">{cellContent}</p>;
        },
      },
      {
        dataField: "actions",
        text: "Actions",
        headerAlign: "center",
        align: "center",
        headerStyle: {
          width: "120px",
        },
        formatter: (cellContent, row) => {
          if (row.slug || row.id) {
            return (
              <div className="d-flex  justify-content-center align-items-center ">
                <ToolTip placement={"top"} content={"View"}>
                  <div
                    className={` d-flex justify-content-center align-items-center bg-primary-hover rounded-3 p-2 mx-2 `}
                    role="button"
                    onClick={() => {
                      navigate(WEB_ROUTES.VEHICLE_DETAILS_MANAGEMENT, {
                        state: row,
                      });
                    }}
                  >
                    <span
                      className={`align-items-center d-flex justify-content-center `}
                    >
                      <IoMdEye />
                    </span>
                  </div>
                </ToolTip>

                <ToolTip placement={"top"} content={"Edit"}>
                  <div
                    className={` d-flex justify-content-center align-items-center bg-primary-hover rounded-3 p-2 mx-2 `}
                    role="button"
                    onClick={() => {
                      setEditedData(row);
                      handleEditModalOpen(true);
                    }}
                  >
                    <span
                      className={`align-items-center d-flex justify-content-center `}
                    >
                      <RiEditFill />
                    </span>
                  </div>
                </ToolTip>

                <ToolTip placement={"top"} content={"Delete "}>
                  <div
                    className="d-flex justify-content-center align-items-center bg-danger-hover rounded-3 p-2 mx-2 "
                    role="button"
                    onClick={() => {
                      setEditedData(row);
                      handleDeleteModalOpen();
                    }}
                  >
                    <span className={`d-flex align-items-center`}>
                      <MdDeleteForever />
                    </span>
                  </div>
                </ToolTip>
              </div>
            );
          }
        },
      },
    ],
    [handleDeleteModalOpen, handleEditModalOpen, navigate]
  );

  return {
    rowData,
    editedData,
    editModal,
    detailModal,
    deleteModal,
    reviewModal,
    currentPage,
    pageSize,
    totalRecords,
    totalPages,
    dataFetched,
    searchQuery,
    isSuccess,
    isLoading,
    isFetching,
    isDeleting,
    refetch,
    indication,
    columns,
    handleSearchChange,
    handleDeleteModalClose,
    handleDetailModalClose,
    handleReviewModalClose,
    handleEditModalClose,
    handleAddUser,
    deleteData,
    setCurrentPage,
    setPageSize,
    setEditedData,
  };
};

export default useVehicleManagement;